import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ArrowLeft, Brain, Heart, Users, Sparkles, Lightbulb, AlertCircle, Shield, Zap, Moon, Activity, HeartHandshake, RefreshCw, ChevronRight, BookOpen, Clock, Calendar, Share2, Twitter, Facebook, Linkedin, Copy, Check, Search } from 'lucide-react';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import { Container } from '../components/brand/BrandLayout';
import { useAuth } from '../contexts/AuthContext';
import CoRegulateLogo from '../components/Logo';
import { useSchema } from '../hooks/useSchema';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping
const ICON_MAP = {
    Brain, Heart, Users, Sparkles, Lightbulb, AlertCircle, Shield,
    Zap, Moon, Activity, HeartHandshake, RefreshCw, BookOpen
};

// SEO-friendly URL slug generator
const generateSlug = (title) => {
    return title
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
};

// Reading time calculator
const calculateReadingTime = (content) => {
    const wordsPerMinute = 200;
    const words = content.split(/\s+/).length;
    return Math.ceil(words / wordsPerMinute);
};

// Blog Post Card
const BlogPostCard = ({ post, index }) => {
    const Icon = ICON_MAP[post.icon] || BookOpen;
    const readingTime = post.reading_time || calculateReadingTime(post.content || '');
    
    const categoryStyles = {
        'adhd-basics': { glassClass: 'glass-card-sage', icon: 'text-[#1D6B6B]' },
        'strategies': { glassClass: 'glass-card-blue', icon: 'text-[#7D8E95]' },
        'mental-health': { glassClass: 'glass-card-lavender', icon: 'text-[#7D8E95]' },
        'relationships': { glassClass: 'glass-card-rose', icon: 'text-[#E08850]' },
        'hypnotherapy': { glassClass: 'glass-card-gold', icon: 'text-[#E08850]' },
        'general': { glassClass: 'glass-card-sage', icon: 'text-[#1D6B6B]' }
    };
    
    const style = categoryStyles[post.category] || categoryStyles.general;

    return (
        <Link 
            to={`/blog/${post.slug}`}
            className={`block ${style.glassClass} rounded-2xl p-6 hover:shadow-lg hover:shadow-[#7D8E95]/10 hover:-translate-y-1 transition-all duration-300 group card-hover`}
            style={{ animationDelay: `${index * 50}ms` }}
            data-testid={`blog-post-${post.slug}`}
        >
            <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl bg-white/60 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className={`w-6 h-6 ${style.icon}`} />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-[#1D6B6B] text-lg group-hover:text-[#145555] transition-colors line-clamp-2">
                        {post.title}
                    </h3>
                    <p className="text-[#7D8E95] text-sm mt-1 line-clamp-2">
                        {post.excerpt}
                    </p>
                    <div className="flex items-center gap-4 mt-3 text-xs text-[#7D8E95]">
                        <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {readingTime} min read
                        </span>
                        {post.category && (
                            <span className="capitalize">{post.category.replace('-', ' ')}</span>
                        )}
                    </div>
                </div>
                <ChevronRight className="w-5 h-5 text-[#7D8E95] opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300 flex-shrink-0 mt-1" />
            </div>
        </Link>
    );
};

// Share Button Component
const ShareButton = ({ url, title }) => {
    const [copied, setCopied] = useState(false);
    
    const shareLinks = {
        twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
        linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
    };
    
    const copyToClipboard = async () => {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };
    
    return (
        <div className="flex items-center gap-2">
            <span className="text-[#7D8E95] text-sm mr-2">Share:</span>
            <a href={shareLinks.twitter} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg bg-white/70 hover:bg-[#FFBB98]/20 transition-colors" aria-label="Share on Twitter">
                <Twitter className="w-4 h-4 text-[#7D8E95]" />
            </a>
            <a href={shareLinks.facebook} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg bg-white/70 hover:bg-[#FFBB98]/20 transition-colors" aria-label="Share on Facebook">
                <Facebook className="w-4 h-4 text-[#7D8E95]" />
            </a>
            <a href={shareLinks.linkedin} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg bg-white/70 hover:bg-[#FFBB98]/20 transition-colors" aria-label="Share on LinkedIn">
                <Linkedin className="w-4 h-4 text-[#7D8E95]" />
            </a>
            <button onClick={copyToClipboard} className="p-2 rounded-lg bg-white/70 hover:bg-[#FFBB98]/20 transition-colors" aria-label="Copy link">
                {copied ? <Check className="w-4 h-4 text-[#1D6B6B]" /> : <Copy className="w-4 h-4 text-[#7D8E95]" />}
            </button>
        </div>
    );
};

// Single Blog Post View
const BlogPostView = ({ posts }) => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    
    const currentUrl = `https://coregulateadhd.com/blog/${slug}`;
    
    // SEO Schema for blog post - called unconditionally
    useSchema(post ? {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": post.title,
        "description": post.excerpt,
        "author": {
            "@type": "Person",
            "name": post.author || "CoRegulate Team"
        },
        "publisher": {
            "@type": "Organization",
            "name": "CoRegulate"
        },
        "datePublished": post.published_at || post.created_at || new Date().toISOString(),
        "mainEntityOfPage": currentUrl
    } : null);
    
    // Set document title directly to avoid react-helmet issues
    useEffect(() => {
        if (post?.title) {
            document.title = post.title + ' | CoRegulate Blog';
        }
        return () => {
            document.title = 'CoRegulate | ADHD Emotional Regulation App';
        };
    }, [post]);
    
    useEffect(() => {
        const fetchPost = async () => {
            try {
                const response = await fetch(`${API_URL}/api/blog/posts/${slug}`);
                if (response.ok) {
                    const data = await response.json();
                    setPost(data);
                }
            } catch (error) {
                console.error('Failed to fetch post:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchPost();
    }, [slug]);
    
    if (loading) {
        return (
            <div className="min-h-screen bg-white/60 flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-[#1D6B6B] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }
    
    if (!post) {
        return (
            <Container className="py-20 text-center">
                <Heading as="h1" size="text-2xl" className="text-[#1D6B6B] mb-4">Article Not Found</Heading>
                <BodyText className="text-[#7D8E95] mb-6">The article you're looking for doesn't exist.</BodyText>
                <Link to="/blog" className="text-[#1D6B6B] hover:underline">← Back to Blog</Link>
            </Container>
        );
    }
    
    const Icon = ICON_MAP[post.icon] || BookOpen;
    const readingTime = post.reading_time || calculateReadingTime(post.content || '');
    
    // Find related posts
    const relatedPosts = posts
        .filter(p => p.slug !== post.slug && p.category === post.category)
        .slice(0, 3);

    return (
        <div className="min-h-screen bg-white/60">
            {/* SEO Meta Tags - title set via useEffect to avoid react-helmet bug */}
            <Helmet>
                <meta name="description" content={post.excerpt || ''} />
                <link rel="canonical" href={currentUrl} />
                <meta property="og:title" content={post.title || 'CoRegulate Blog'} />
                <meta property="og:description" content={post.excerpt || ''} />
                <meta property="og:type" content="article" />
                <meta property="og:url" content={currentUrl} />
                <meta property="article:published_time" content={post.published_at || post.created_at || ''} />
                <meta property="article:author" content={post.author || "CoRegulate Team"} />
                <meta property="article:section" content={post.category?.replace('-', ' ') || ''} />
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:title" content={post.title || 'CoRegulate Blog'} />
                <meta name="twitter:description" content={post.excerpt || ''} />
            </Helmet>
            
            {/* Header */}
            <header className="sticky top-0 z-50 bg-white/60/95 backdrop-blur-sm border-b border-white/40/30">
                <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
                    <Link to="/landing" className="flex items-center gap-2">
                        <CoRegulateLogo size="small" />
                    </Link>
                    <Link 
                        to="/blog"
                        className="flex items-center gap-2 text-[#7D8E95] hover:text-[#1D6B6B] transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        <span className="text-sm">All Articles</span>
                    </Link>
                </div>
            </header>
            
            {/* Visual Breadcrumbs */}
            <nav className="max-w-3xl mx-auto px-4 py-3" aria-label="Breadcrumb">
                <ol className="flex items-center gap-2 text-sm text-[#7D8E95]">
                    <li>
                        <Link to="/landing" className="hover:text-[#1D6B6B] transition-colors">Home</Link>
                    </li>
                    <li className="flex items-center gap-2">
                        <ChevronRight className="w-3 h-3" />
                        <Link to="/blog" className="hover:text-[#1D6B6B] transition-colors">Blog</Link>
                    </li>
                    <li className="flex items-center gap-2">
                        <ChevronRight className="w-3 h-3" />
                        <span className="text-[#1D6B6B] font-medium truncate max-w-[200px]">{post.title}</span>
                    </li>
                </ol>
            </nav>
            
            {/* Article */}
            <article className="max-w-3xl mx-auto px-4 py-12">
                {/* Article Header */}
                <header className="mb-8">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#1D6B6B] to-[#145555] flex items-center justify-center">
                            <Icon className="w-5 h-5 text-white" />
                        </div>
                        <span className="text-sm text-[#7D8E95] capitalize">{post.category?.replace('-', ' ')} • {readingTime} min read</span>
                    </div>
                    <h1 className="text-3xl md:text-4xl font-semibold text-[#1D6B6B] mb-4" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                        {post.title}
                    </h1>
                    <p className="text-lg text-[#7D8E95]">
                        {post.excerpt}
                    </p>
                </header>
                
                {/* Share */}
                <div className="mb-8 pb-8 border-b border-white/40/30">
                    <ShareButton url={currentUrl} title={post.title} />
                </div>
                
                {/* Article Content */}
                <div className="prose prose-lg max-w-none">
                    {(post.content || '').split('\n\n').map((paragraph, index) => {
                        // Check if it's a header (starts with ##)
                        if (paragraph.startsWith('## ')) {
                            return (
                                <h2 key={index} className="text-xl font-semibold text-[#1D6B6B] mt-8 mb-4" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                                    {paragraph.replace('## ', '')}
                                </h2>
                            );
                        }
                        // Check if it's a subheader (starts with **text**)
                        if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                            return (
                                <h3 key={index} className="text-lg font-semibold text-[#1D6B6B] mt-6 mb-2">
                                    {paragraph.replace(/\*\*/g, '')}
                                </h3>
                            );
                        }
                        // Check if it's a list
                        if (paragraph.includes('\n•') || paragraph.startsWith('•')) {
                            const lines = paragraph.split('\n');
                            return (
                                <div key={index} className="my-4">
                                    {lines[0] && !lines[0].startsWith('•') && (
                                        <p className="text-[#3A3A37] mb-2">{lines[0]}</p>
                                    )}
                                    <ul className="list-none space-y-2">
                                        {lines.filter(l => l.startsWith('•')).map((item, i) => (
                                            <li key={i} className="flex items-start gap-2 text-[#3A3A37]">
                                                <span className="text-[#1D6B6B] mt-1.5">•</span>
                                                <span>{item.replace('•', '').trim()}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            );
                        }
                        // Check for horizontal rule
                        if (paragraph.trim() === '---') {
                            return <hr key={index} className="my-8 border-white/40/30" />;
                        }
                        // Check for italic (starts with *)
                        if (paragraph.startsWith('*') && paragraph.endsWith('*') && !paragraph.startsWith('**')) {
                            return (
                                <p key={index} className="text-[#7D8E95] italic leading-relaxed mb-4">
                                    {paragraph.replace(/^\*|\*$/g, '')}
                                </p>
                            );
                        }
                        return (
                            <p key={index} className="text-[#3A3A37] leading-relaxed mb-4">
                                {paragraph}
                            </p>
                        );
                    })}
                </div>
                
                {/* CTA */}
                <div className="mt-12 p-8 bg-gradient-to-br from-[#1D6B6B] to-[#145555] rounded-2xl text-center">
                    <h3 className="text-xl font-semibold text-white mb-2" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                        Ready to Start Regulating?
                    </h3>
                    <p className="text-white/80 mb-4">
                        CoRegulate offers practical tools for ADHD emotional regulation.
                    </p>
                    <Link 
                        to="/landing"
                        className="inline-block px-6 py-3 bg-white text-[#1D6B6B] font-medium rounded-full hover:bg-white/60 transition-colors"
                    >
                        Try CoRegulate Free
                    </Link>
                </div>
                
                {/* Related Posts */}
                {relatedPosts.length > 0 && (
                    <div className="mt-12">
                        <h3 className="text-xl font-semibold text-[#1D6B6B] mb-6" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                            Related Articles
                        </h3>
                        <div className="grid gap-4">
                            {relatedPosts.map((relatedPost, index) => (
                                <BlogPostCard key={relatedPost.id} post={relatedPost} index={index} />
                            ))}
                        </div>
                    </div>
                )}
            </article>
            
            {/* Footer */}
            <footer className="bg-[#1D6B6B] text-white py-8 mt-12">
                <div className="max-w-4xl mx-auto px-4 text-center">
                    <CoRegulateLogo size="small" variant="light" />
                    <p className="text-white/60 text-sm mt-4">
                        Made for ADHD hearts, shaped by ADHD minds.
                    </p>
                </div>
            </footer>
        </div>
    );
};

// Blog Index Page
const BlogIndex = () => {
    const { isAuthenticated } = useAuth();
    const [posts, setPosts] = useState([]);
    const [filteredPosts, setFilteredPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const { slug } = useParams();
    
    // Logo links to dashboard if authenticated
    const logoHref = isAuthenticated ? '/home' : '/';
    
    // SEO Schema for blog index
    useSchema({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": "CoRegulate Blog",
        "description": "Expert articles on ADHD emotional regulation, relationships, and mental health.",
        "url": "https://coregulateadhd.com/blog"
    });
    
    // Scroll to top on mount
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);
    
    useEffect(() => {
        const fetchPosts = async () => {
            try {
                const response = await fetch(`${API_URL}/api/blog/posts`);
                if (response.ok) {
                    const data = await response.json();
                    const visiblePosts = data.posts || [];
                    setPosts(visiblePosts);
                    setFilteredPosts(visiblePosts);
                }
            } catch (error) {
                console.error('Failed to fetch blog posts:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchPosts();
    }, []);
    
    useEffect(() => {
        let filtered = posts;
        
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(post => 
                post.title?.toLowerCase().includes(query) ||
                post.excerpt?.toLowerCase().includes(query) ||
                post.content?.toLowerCase().includes(query) ||
                post.tags?.some(tag => tag.toLowerCase().includes(query))
            );
        }
        
        if (selectedCategory !== 'all') {
            filtered = filtered.filter(post => post.category === selectedCategory);
        }
        
        setFilteredPosts(filtered);
    }, [searchQuery, selectedCategory, posts]);
    
    // If viewing a specific post
    if (slug) {
        return <BlogPostView posts={posts} />;
    }
    
    // Separate featured (first) post from the rest
    const featuredPost = filteredPosts[0];
    const otherPosts = filteredPosts.slice(1);

    return (
        <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #FBE0C3 0%, #FEF6EF 100%)' }}>
            {/* SEO Meta Tags for Blog Index */}
            <Helmet>
                <title>Blog | CoRegulate - ADHD Emotional Regulation</title>
                <meta name="description" content="Expert articles on ADHD emotional regulation, relationships, and mental health. Evidence-based information and practical strategies for ADHD adults." />
                <link rel="canonical" href="https://coregulateadhd.com/blog" />
                <meta property="og:title" content="Blog | CoRegulate" />
                <meta property="og:description" content="Expert articles on ADHD emotional regulation, relationships, and mental health strategies." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://coregulateadhd.com/blog" />
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:title" content="Blog | CoRegulate" />
                <meta name="twitter:description" content="Expert articles on ADHD emotional regulation and mental health." />
            </Helmet>
            
            {/* Header */}
            <header className="glass-header sticky top-0 z-50">
                <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
                    <Link to={logoHref} className="flex items-center gap-2 flex-shrink-0" data-testid="header-logo">
                        <CoRegulateLogo size="small" />
                    </Link>
                    {isAuthenticated ? (
                        <Link 
                            to="/home"
                            className="px-3 py-1.5 sm:px-4 sm:py-2 bg-gradient-to-r from-[#1D6B6B] to-[#145555] text-white text-xs sm:text-sm font-medium rounded-full hover:shadow-lg transition-all flex-shrink-0 ml-2"
                        >
                            Dashboard
                        </Link>
                    ) : (
                        <Link 
                            to="/auth"
                            className="px-3 py-1.5 sm:px-4 sm:py-2 bg-gradient-to-r from-[#1D6B6B] to-[#145555] text-white text-xs sm:text-sm font-medium rounded-full hover:shadow-lg transition-all flex-shrink-0 ml-2"
                        >
                            <span className="hidden sm:inline">Try CoRegulate</span>
                            <span className="sm:hidden">Try Free</span>
                        </Link>
                    )}
                </div>
            </header>
            
            {/* Main Content */}
            <main className="max-w-5xl mx-auto px-4 py-8 md:py-12">
                {loading ? (
                    <div className="text-center py-20">
                        <div className="w-10 h-10 border-2 border-[#1D6B6B] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                        <p className="text-[#7D8E95]">Loading articles...</p>
                    </div>
                ) : !featuredPost ? (
                    <div className="text-center py-20">
                        <BookOpen className="w-16 h-16 text-[#7D8E95] mx-auto mb-4" />
                        <p className="text-[#7D8E95] text-lg">No articles yet.</p>
                    </div>
                ) : (
                    <>
                        {/* Featured Article - Same card style as others, full width */}
                        <section className="mb-12">
                            <Link 
                                to={`/blog/${featuredPost.slug}`}
                                className="group bg-white/60 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/50 hover:border-[#1D6B6B]/30 hover:shadow-lg transition-all block"
                            >
                                {/* Card Image - Same structure as other cards */}
                                <div className="h-48 md:h-56 overflow-hidden relative">
                                    <img 
                                        src="https://images.unsplash.com/photo-1770954098598-418c6f08324a?w=1200&q=80" 
                                        alt=""
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    />
                                    {/* Featured Badge */}
                                    <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-[#1D6B6B]/90 backdrop-blur-sm px-3 py-1.5 rounded-full">
                                        <Sparkles className="w-3.5 h-3.5 text-[#FFBB98]" />
                                        <span className="text-white text-xs font-medium">Featured</span>
                                    </div>
                                </div>
                                
                                {/* Card Content - Same structure as other cards */}
                                <div className="p-6">
                                    {/* Category & Reading Time */}
                                    <div className="flex items-center gap-3 text-sm text-[#7D8E95] mb-3">
                                        <span className="uppercase tracking-wide text-[#1D6B6B] font-medium text-xs">
                                            {featuredPost.category?.replace('-', ' ') || 'ADHD'}
                                        </span>
                                        <span>•</span>
                                        <span>{featuredPost.reading_time || calculateReadingTime(featuredPost.content || '')} min read</span>
                                    </div>
                                    
                                    {/* Title */}
                                    <h3 className="text-xl font-semibold text-[#344648] mb-3 group-hover:text-[#1D6B6B] transition-colors leading-snug" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                                        {featuredPost.title}
                                    </h3>
                                    
                                    {/* Excerpt */}
                                    <p className="text-[#7D8E95] text-sm leading-relaxed line-clamp-2">
                                        {featuredPost.excerpt}
                                    </p>
                                    
                                    {/* Read More */}
                                    <div className="mt-4 flex items-center gap-2 text-[#1D6B6B] font-medium text-sm group-hover:gap-3 transition-all">
                                        Read article
                                        <ChevronRight className="w-4 h-4" />
                                    </div>
                                </div>
                            </Link>
                        </section>
                        
                        {/* More Articles Section */}
                        {otherPosts.length > 0 && (
                            <section>
                                {/* Section Header */}
                                <div className="flex items-center gap-4 mb-8">
                                    <h2 className="text-2xl font-semibold text-[#344648]" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                                        More Articles
                                    </h2>
                                    <div className="flex-1 h-px bg-gradient-to-r from-[#1D6B6B]/20 to-transparent" />
                                </div>
                                
                                {/* Articles Grid */}
                                <div className="grid md:grid-cols-2 gap-6">
                                    {otherPosts.map((post, index) => {
                                        const readingTime = post.reading_time || calculateReadingTime(post.content || '');
                                        // Different abstract images for variety
                                        const cardImages = [
                                            "https://images.unsplash.com/photo-1770954098628-182216af4d85?w=400&q=80",
                                            "https://images.unsplash.com/photo-1764386240887-a97595e90987?w=400&q=80"
                                        ];
                                        
                                        return (
                                            <Link 
                                                key={post.id}
                                                to={`/blog/${post.slug}`}
                                                className="group bg-white/60 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/50 hover:border-[#1D6B6B]/30 hover:shadow-lg transition-all"
                                            >
                                                {/* Card Image */}
                                                <div className="h-32 overflow-hidden">
                                                    <img 
                                                        src={cardImages[index % cardImages.length]}
                                                        alt=""
                                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                                    />
                                                </div>
                                                
                                                {/* Card Content */}
                                                <div className="p-6">
                                                    {/* Category & Reading Time */}
                                                    <div className="flex items-center gap-3 text-sm text-[#7D8E95] mb-3">
                                                        <span className="uppercase tracking-wide text-[#1D6B6B] font-medium text-xs">
                                                            {post.category?.replace('-', ' ') || 'ADHD'}
                                                        </span>
                                                        <span>•</span>
                                                        <span>{readingTime} min</span>
                                                    </div>
                                                    
                                                    {/* Title */}
                                                    <h3 className="text-xl font-semibold text-[#344648] mb-3 group-hover:text-[#1D6B6B] transition-colors leading-snug" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                                                        {post.title}
                                                    </h3>
                                                    
                                                    {/* Excerpt */}
                                                    <p className="text-[#7D8E95] text-sm leading-relaxed line-clamp-2">
                                                        {post.excerpt}
                                                    </p>
                                                    
                                                    {/* Read More */}
                                                    <div className="mt-4 flex items-center gap-2 text-[#1D6B6B] font-medium text-sm group-hover:gap-3 transition-all">
                                                        Read article
                                                        <ChevronRight className="w-4 h-4" />
                                                    </div>
                                                </div>
                                            </Link>
                                        );
                                    })}
                                </div>
                            </section>
                        )}
                        
                        {/* Newsletter / CTA Section */}
                        <section className="mt-16 text-center">
                            <div className="bg-white/40 backdrop-blur-sm rounded-2xl p-8 md:p-12 border border-white/50">
                                <h2 className="text-2xl md:text-3xl font-semibold text-[#344648] mb-4" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                                    Ready to Start Regulating?
                                </h2>
                                <p className="text-[#7D8E95] mb-6 max-w-xl mx-auto">
                                    CoRegulate offers practical tools designed specifically for ADHD emotional regulation. 
                                    Start your journey today.
                                </p>
                                <Link 
                                    to="/auth"
                                    className="inline-flex items-center gap-2 text-white px-8 py-3 rounded-full font-semibold hover:shadow-lg hover:opacity-90 transition-all"
                                    style={{
                                        background: 'linear-gradient(135deg, #7D8E95 0%, #2A8585 35%, #1D6B6B 65%, #FFBB98 100%)'
                                    }}
                                >
                                    Get Started Free
                                    <ChevronRight className="w-4 h-4" />
                                </Link>
                            </div>
                        </section>
                    </>
                )}
            </main>
            
            {/* Footer */}
            <footer className="glass-header border-t-0 py-12 mt-8">
                <div className="max-w-5xl mx-auto px-4 text-center">
                    <CoRegulateLogo size="small" />
                    <p className="text-[#7D8E95] text-sm mt-4 mb-6">
                        Made for ADHD hearts, shaped by ADHD minds.
                    </p>
                    <div className="flex justify-center gap-6 text-sm text-[#7D8E95]">
                        <Link to={logoHref} className="hover:text-[#1D6B6B] transition-colors">Home</Link>
                        <Link to="/about" className="hover:text-[#1D6B6B] transition-colors">About</Link>
                        <Link to="/pricing" className="hover:text-[#1D6B6B] transition-colors">Pricing</Link>
                        <Link to="/contact" className="hover:text-[#1D6B6B] transition-colors">Contact</Link>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default BlogIndex;
